import * as React from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useContext, useEffect, useState } from "react";

import { InputLabel, MenuItem, Select, Box, Chip, Divider, Paper, Container, Fade, CircularProgress, Grid, Menu, MenuList, ListItemIcon, ListItemText, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Checkbox, FormControlLabel, Card, CardContent, Typography, TextField, Button, Avatar, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';

import { deleteMyAccount, clearLocalStorage, fetchMainUserByEmail } from "../../utils/api/api";
import { compressImage } from "../../utils/utils/imageUtils";
import { checkMembershipStatus } from "../../utils/utils/membershipUtil";
import { getImageSrc } from "../../utils/utils/profileUtils";
import { uploadUserProfile } from "../../utils/utils/supabaseStorage";
import SupabaseImageAvatar from "../../utils/components/SupabaseImageAvatar";
import { getBeltDisplayName } from "../../utils/utils/membersBeltUtils";
import { RankBeltDisplay } from "../../utils/components/belt/BeltRankVisual";
import { useValidationBeltSystem } from "../../hooks/useValidationBeltSystem";
import { useActiveMemberSport } from "../../hooks/useActiveMemberSport";
import { memberShowsBeltRankMenu } from "../../utils/memberSport";
import { shouldUseBeltView } from "../../utils/beltVisualCatalog";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import SportsKabaddi from "@mui/icons-material/SportsKabaddi";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FamilyIcon from "@mui/icons-material/FamilyRestroom";
import MembershipIcon from "@mui/icons-material/GroupAdd"; 
import { has } from "lodash";
import localforage from 'localforage';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VerifiedIcon from '@mui/icons-material/Verified';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AppsIcon from '@mui/icons-material/Apps';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import { useTheme } from "../../contexts/ThemeContext";
import { useSystemButtonStyles } from "../../hooks/useSystemButtonStyles";
import { SnackbarNotification } from "../../utils/components/SnackbarNotification";
import PageLoader from "../../utils/components/PageLoader";
import { auth } from "../../supabase/client";
import { SupabaseUserContext } from "../../contexts/supabaseUser.context";
import InputValidator from "../../utils/security/inputValidator";
import LockIcon from "@mui/icons-material/Lock";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { getMm, getPageTitleSx } from "../../theme/modernMaterialEnterprise";

const PROFILE_CARD_MAX_WIDTH = { xs: "100%", sm: "440px" };

function ProfileQuickActionsBar({
  anchorEl,
  setAnchorEl,
  isDarkMode,
  t,
  setSelectedPage,
  setChangePasswordDialogOpen,
  onOpenDeleteAccountDialog,
  canDeleteAccount,
  isMember,
  isParent,
  isStudent,
  userInfo,
}) {
  const { outlineButtonSx } = useSystemButtonStyles();
  const user = userInfo?.[0];
  if (!user) return null;

  const menuItemSx = {
    py: 1.5,
    px: 2,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    "&:hover": { backgroundColor: getMm(isDarkMode).outlineVariant },
  };

  const itemTextProps = {
    fontWeight: 500,
    color: getMm(isDarkMode).onSurface,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  };

  const close = () => setAnchorEl(null);

  return (
    <Box sx={{ width: "100%", maxWidth: PROFILE_CARD_MAX_WIDTH, mx: "auto", mb: { xs: 1, sm: 2 } }}>
      <Button
        fullWidth
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<AppsIcon />}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          ...outlineButtonSx,
          minHeight: { xs: 40, sm: 48 },
          py: { xs: 0.75, sm: 1.25 },
          px: 2,
          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        {t("quick_actions")}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        PaperProps={{
          sx: {
            mt: 0.75,
            width: anchorEl?.offsetWidth,
            maxWidth: "100%",
            borderRadius: "8px",
            boxShadow: "none",
            border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
            backgroundColor: getMm(isDarkMode).surfaceLowest,
            overflow: "hidden",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          },
        }}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <MenuList sx={{ py: 0.5 }}>
          {!isMember && isParent && (
            <MenuItem
              onClick={() => {
                setSelectedPage("ProfileManageFamily");
                close();
              }}
              sx={menuItemSx}
            >
              <ListItemIcon>
                <PeopleIcon sx={{ color: getMm(isDarkMode).onSurface }} />
              </ListItemIcon>
              <ListItemText primary={t("manage_family_members")} primaryTypographyProps={itemTextProps} />
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setSelectedPage("ProfileAttendancePage");
              close();
            }}
            sx={menuItemSx}
          >
            <ListItemIcon>
              <TrendingUpIcon sx={{ color: getMm(isDarkMode).onSurface }} />
            </ListItemIcon>
            <ListItemText primary={t("attendance")} primaryTypographyProps={itemTextProps} />
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSelectedPage("MyPaymentsPage");
              close();
            }}
            sx={menuItemSx}
          >
            <ListItemIcon>
              <ReceiptLongIcon sx={{ color: getMm(isDarkMode).onSurface }} />
            </ListItemIcon>
            <ListItemText primary={t("my_payments_title")} primaryTypographyProps={itemTextProps} />
          </MenuItem>
          {isStudent && (
            <MenuItem
              onClick={() => {
                setSelectedPage("AchievementsPage");
                close();
              }}
              sx={menuItemSx}
            >
              <ListItemIcon>
                <EmojiEventsIcon sx={{ color: getMm(isDarkMode).onSurface }} />
              </ListItemIcon>
              <ListItemText primary={t("achievements")} primaryTypographyProps={itemTextProps} />
            </MenuItem>
          )}
          {isStudent && (user.coachId || user.coachID) && (
            <MenuItem
              onClick={() => {
                setSelectedPage("MyMentorPage");
                close();
              }}
              sx={menuItemSx}
            >
              <ListItemIcon>
                <SupervisorAccountIcon sx={{ color: getMm(isDarkMode).onSurface }} />
              </ListItemIcon>
              <ListItemText primary={t("my_mentor")} primaryTypographyProps={itemTextProps} />
            </MenuItem>
          )}
          {(user.coachId || user.coachID) && (
            <MenuItem
              onClick={() => {
                setSelectedPage("MySmiliesPage");
                close();
              }}
              sx={menuItemSx}
            >
              <ListItemIcon>
                <SentimentSatisfiedAltIcon sx={{ color: getMm(isDarkMode).onSurface }} />
              </ListItemIcon>
              <ListItemText primary={t("my_smilies")} primaryTypographyProps={itemTextProps} />
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setChangePasswordDialogOpen(true);
              close();
            }}
            sx={menuItemSx}
          >
            <ListItemIcon>
              <LockIcon sx={{ color: getMm(isDarkMode).onSurface }} />
            </ListItemIcon>
            <ListItemText primary={t("change_password")} primaryTypographyProps={itemTextProps} />
          </MenuItem>
          {canDeleteAccount ? (
            <MenuItem
              onClick={() => {
                onOpenDeleteAccountDialog();
                close();
              }}
              sx={menuItemSx}
            >
              <ListItemIcon>
                <DeleteOutlineIcon sx={{ color: "#DC2626" }} />
              </ListItemIcon>
              <ListItemText
                primary={t("delete_my_account")}
                primaryTypographyProps={{ ...itemTextProps, color: "#DC2626" }}
              />
            </MenuItem>
          ) : null}
        </MenuList>
      </Menu>
    </Box>
  );
}

const ProfilePage = ({
  selectedPage,
  setSelectedPage,
  userInfo,
  setUserInfo,
  allowEdit = false,
  isLoading: globalLoading,
  setIsLoading: setGlobalLoading,
}) => {
  const navigate = useNavigate();
  const { logOutUser } = useContext(SupabaseUserContext);
  const {
    validationKey,
    rankUiConfigured,
    sportUsesStripeRank: memberUsesStripeRank,
    apiMeLoaded,
  } = useValidationBeltSystem();
  const { sport: activeMemberSport, loaded: activeMemberSportLoaded } = useActiveMemberSport(userInfo);
  const showMemberBeltRank = memberShowsBeltRankMenu(activeMemberSport, {
    loaded: activeMemberSportLoaded,
  });
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const {
    primaryButtonSx,
    cancelButtonSx,
    outlineButtonSx,
    secondaryButtonSx,
    destructiveButtonSx,
    destructiveOutlineButtonSx,
    greenSmileyButtonSx,
    redSmileyButtonSx,
    iconActionSx,
    iconActionTableSx,
    iconActionLargeSx,
    toolbarButtonSx,
    dialogPrimarySx,
    dialogCancelSx,
    dialogFormPrimarySx,
    dialogFormCancelSx,
    dialogFormDestructiveSx,
    warningButtonSx,
    buttonSx,
  } = useSystemButtonStyles();
  const [status, setStatus] = useState(null);
  const [medal, setMedal] = useState("");
  const [expireDate, setExpireDate] = useState(null);
  const [membershipType, setMembershipType] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [name, setName] = useState("");
  const [belt, setBelt] = useState("");
  const [academy, setAcademy] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [image, setImage] = useState(null);
  const [imageName, setImageName] = useState("");
  const [address, setAddress] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (setGlobalLoading && selectedPage === 'ProfilePage') setGlobalLoading(isLoading);
  }, [isLoading, selectedPage, setGlobalLoading]);
  const [isStudent, setIsStudent] = useState(false);
  const [isParent, setIsParent] = useState(false);
  const { t } = useTranslation();
  const showProfileUpdateReminder = !isDisabled;
  const isMobile = useMediaQuery("(max-width:600px)");
  const [hasParent, setHasParent] = useState(false);
  const token = window.localStorage.getItem("realmJwt");
  const [quickActionsAnchor, setQuickActionsAnchor] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteAccountAcknowledged, setDeleteAccountAcknowledged] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);

  const openDeleteAccountDialog = () => {
    setDeleteAccountAcknowledged(false);
    setDeleteAccountDialogOpen(true);
  };

  const closeDeleteAccountDialog = () => {
    if (deleteAccountLoading) return;
    setDeleteAccountDialogOpen(false);
    setDeleteAccountAcknowledged(false);
  };

  const activeUser = userInfo?.[0];
  const canDeleteAccount = Boolean(
    activeUser &&
      !activeUser.isAdmin &&
      !activeUser.isCoach &&
      !activeUser.archived_at
  );
  useEffect(() => {
    if (selectedPage === "ProfilePage") {
      if (userInfo && userInfo[0]) {
        setName(userInfo[0].name || "");
        setBelt(userInfo[0].belt ? (userInfo[0].belt.charAt(0).toUpperCase() + userInfo[0].belt.slice(1)) : "");
        setHasParent(userInfo[0].hasParent || false);
        setAcademy(userInfo[0].clubname || "");
        setAddress(userInfo[0].address || "");
        setImage(userInfo[0].image);
        setImageName("");
        setIsStudent(userInfo[0].isStudent || false);
        setIsParent(userInfo[0].isParent || false);
        const { status, isDisabled: membershipDisabled, membershipType, medal, expireDate, isMember } =
          checkMembershipStatus(userInfo[0]);
        setStatus(status);
        // Allow editing if allowEdit prop is true OR if membership allows editing
        setIsDisabled(!allowEdit && membershipDisabled);
        setMembershipType(membershipType);
        setMedal(medal);
        setExpireDate(expireDate);
        setIsMember(isMember);
        setIsLoading(false);
      }
    }
  }, [userInfo, selectedPage, allowEdit]);

  // Helper: convert File to base64 for preview
  const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = async (event) => {
    console.log('🖼️ handleImageChange triggered', event);
    const file = event.target.files[0];
    console.log('📁 Selected file:', file);
    if (file) {
      setImage(file);
      setImageName(file.name);

      // For preview, convert File to base64 and store in tempImagePreview
      const dataUrl = await fileToDataUrl(file);
      setTempImagePreview(dataUrl);
    }
  };

  // Add state for temporary image preview (when selecting new image)
  const [tempImagePreview, setTempImagePreview] = useState(null);
  // Add state for cache-busting image refresh - check sessionStorage for persisted value
  const [imageRefreshKey, setImageRefreshKey] = useState(() => {
    const stored = sessionStorage.getItem('profileImageRefresh');
    return stored ? parseInt(stored, 10) : 0;
  });

  // When the userInfo changes and new image is loaded from server, reset preview
  useEffect(() => {
    setTempImagePreview(null);
  }, [userInfo, selectedPage]);
  
  // Clear sessionStorage refresh key when component unmounts or user changes
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('profileImageRefresh');
    };
  }, []);

  const updateProfile = async (event) => {
    event.preventDefault();
    const formData = new FormData();
    formData.append("email", userInfo[0].email);
    formData.append("studentID", userInfo[0].studentID);
    formData.append("belt", belt);
    formData.append("academy", academy);
    formData.append("address", address);
    formData.append("isParent", isParent);
    formData.append("isStudent", isStudent);
    
    // Handle image upload to Supabase storage
    if (image && image instanceof File) {
      try {
        setSnackbarMessage("Uploading image to Supabase Storage...");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
        
        // Compress the image first
        const compressedImage = await compressImage(image);
        const compressedFile = new File([compressedImage], image.name, { type: image.type });
        
        // Convert to base64 for Supabase upload
        const base64Image = await fileToDataUrl(compressedFile);
        const base64String = base64Image.split(',')[1]; // Remove data:image/...;base64, prefix
        
        // Upload to Supabase storage
        const uploadResult = await uploadUserProfile(
          base64String, 
          userInfo[0].studentID, 
          image.type
        );
        
        if (uploadResult.success) {
          // Add the Supabase URL to the form data instead of the file
          formData.append("imageUrl", uploadResult.data.publicUrl);
          setSnackbarMessage("Image uploaded successfully! Updating profile...");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        } else {
          setSnackbarMessage(`Failed to upload image: ${uploadResult.error}`);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
          return;
        }
      } catch (error) {
        console.error('Error uploading image to Supabase:', error);
        setSnackbarMessage("Error uploading image to Supabase storage");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_CONNECTION_API}/api/updateProfile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "Authorization": `Bearer ${token}`,
          }
        }
      );

      setSnackbarMessage("Profile updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      
      // Reset image preview state immediately
      setTempImagePreview(null);
      setImage(null);
      setImageName("");
      
      // Set a timestamp-based refresh key to force image cache refresh
      // This ensures the new image is loaded even after page reload
      const timestamp = Date.now();
      setImageRefreshKey(timestamp);
      
      // Store the timestamp in sessionStorage so it persists after reload
      sessionStorage.setItem('profileImageRefresh', timestamp.toString());
      
      // Refresh userInfo from backend to get updated image URL
      try {
        const email = userInfo[0].email;
        if (email) {
          // Wait a moment for backend to process the update
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Fetch updated user data
          const updatedUserData = await fetchMainUserByEmail(email);
          const freshUser = Array.isArray(updatedUserData) ? updatedUserData[0] : updatedUserData;
          
          if (freshUser) {
            // Update local state with fresh data
            const updatedUserInfo = [freshUser];
            setUserInfo(updatedUserInfo);
            
            // Update localforage cache
            await localforage.setItem('userInfo', updatedUserInfo);
            await localforage.setItem('activeUser', freshUser);
            
            // Update local component state
            setName(freshUser.name || "");
            setAddress(freshUser.address || "");
            setAcademy(freshUser.clubname || "");
            if (freshUser.image) {
              setImage(freshUser.image);
            }
            
            // Small delay to let state update and image load, then show success
            setTimeout(() => {
              setSnackbarMessage(t("profile_updated_successfully") || "Profile updated successfully! New image should now be visible.");
              setSnackbarSeverity("success");
              setSnackbarOpen(true);
            }, 500);
            
            return; // Don't reload the page, just refresh the data
          }
        }
      } catch (refreshError) {
        console.error('Error refreshing user data after update:', refreshError);
        // Fall through to reload if refresh fails
      }
      
      // Fallback: clear cache and reload if refresh fails
      try {
        await Promise.all([
          localforage.removeItem('activeUser'),
          localforage.removeItem('userInfo'),
        ]);
      } catch (err) {
        console.error('Error clearing IndexedDB:', err);
      }

      localStorage.removeItem('club');
      localStorage.removeItem('email');
      localStorage.removeItem('name');
      window.location.reload();
    } catch (error) {
      setSnackbarMessage("Error updating profile");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPassword.trim()) {
      setSnackbarMessage(t("change_password_enter_new") || "Please enter a new password.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (newPassword !== confirmPassword) {
      setSnackbarMessage(t("change_password_mismatch") || "Passwords do not match.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    const validation = InputValidator.validatePassword(newPassword);
    if (!validation.isValid) {
      setSnackbarMessage(validation.error);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setChangePasswordLoading(true);
    setSnackbarOpen(false);
    try {
      const { error } = await auth.updateUser({ password: newPassword.trim() });
      if (error) {
        setSnackbarMessage(error.message || (t("change_password_error") || "Failed to update password."));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(t("change_password_success") || "Password updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setNewPassword("");
        setConfirmPassword("");
        setChangePasswordDialogOpen(false);
      }
    } catch (err) {
      console.error("Change password error:", err);
      setSnackbarMessage(t("change_password_error") || "Failed to update password. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const user = userInfo?.[0];
    if (!user) return;

    if (!deleteAccountAcknowledged) {
      setSnackbarMessage(t("delete_account_consent_required"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setDeleteAccountLoading(true);
    setSnackbarOpen(false);
    try {
      await deleteMyAccount(user.studentID, {
        consent: true,
        confirmEmail: user.email,
        clubID: user.clubID ?? user.clubId,
      });
      await logOutUser();
      clearLocalStorage();
      window.location.href = "/login";
    } catch (err) {
      setSnackbarMessage(
        err?.response?.data?.error || err.message || t("delete_account_failed")
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  if (selectedPage !== "ProfilePage") return null;

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Determine which image to show in Avatar
  let profileImgSrc = null;
  if (tempImagePreview) {
    profileImgSrc = tempImagePreview;
  } else if (image && !(image instanceof File)) {
    profileImgSrc = getImageSrc(image?.data || image);
  } else {
    profileImgSrc = undefined;
  }

  // Admin Dashboard styles (dark mode supported)
  const getCardStyle = () => ({
    backgroundColor: getMm(isDarkMode).surfaceLowest,
    border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
    borderRadius: '8px',
    boxShadow: 'none',
    fontFamily: "'Plus Jakarta Sans', sans-serif"
  });

  const getActionCardStyle = () => ({
    backgroundColor: '#374151',
    color: 'white',
    borderRadius: '8px',
    boxShadow: 'none',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    height: '100px',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    '&:hover': { backgroundColor: '#374151' }
  });

  const getTextFieldStyle = () => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: '8px',
      background: getMm(isDarkMode).surfaceLowest,
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      "& fieldset": {
        borderColor: getMm(isDarkMode).outlineVariant,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#374151",
      },
    },
    "& .MuiInputLabel-root": {
      color: getMm(isDarkMode).onSurfaceVariant,
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    },
    "& .MuiInputBase-input": {
      color: getMm(isDarkMode).onSurface,
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    },
  });

  return (
    <Box sx={{ 
      maxWidth: { xs: '100%', sm: 900 },
      mx: "auto", 
      px: { xs: 0.75, sm: 3 },
      py: { xs: 1, sm: 3 },
      width: '100%',
      minHeight: { xs: 'auto', sm: '100vh' },
    }}>
      {isLoading ? (
        <PageLoader message={t("loading") || "Loading..."} />
      ) : (
        <Fade in timeout={800}>
          <Box sx={{ pb: { xs: 2, sm: 10 }, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {/* Header Section - Admin Dashboard Style */}
            <Box sx={{ position: 'relative', textAlign: "center", mb: { xs: 1, sm: 1.5 } }}>
<Typography sx={{ ...getPageTitleSx(isDarkMode), mb: { xs: 0.25, sm: 1 } }}>
                {t("profile")}
              </Typography>
              <Typography sx={{ 
                color: getMm(isDarkMode).onSurfaceVariant,
                fontWeight: 500,
                fontSize: { xs: '0.8125rem', sm: '1.125rem' },
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                display: { xs: 'none', sm: 'block' },
              }}>
                {t("manage_your_account")}
              </Typography>
            </Box>

            {userInfo?.[0] && (
              <ProfileQuickActionsBar
                anchorEl={quickActionsAnchor}
                setAnchorEl={setQuickActionsAnchor}
                isDarkMode={isDarkMode}
                t={t}
                setSelectedPage={setSelectedPage}
                setChangePasswordDialogOpen={setChangePasswordDialogOpen}
                onOpenDeleteAccountDialog={openDeleteAccountDialog}
                canDeleteAccount={canDeleteAccount}
                isMember={isMember}
                isParent={isParent}
                isStudent={isStudent}
                userInfo={userInfo}
              />
            )}

            {/* Profile Update Reminder */}
            {showProfileUpdateReminder && (
              <Card sx={{
                backgroundColor: getMm(isDarkMode).surfaceLowest,
                border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                borderRadius: '8px',
                boxShadow: 'none',
                mb: 4,
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                <CardContent sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 2, 
                  py: 3,
                  px: 3,
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}>
                  <InfoOutlinedIcon sx={{ 
                    fontSize: isMobile ? 28 : 32, 
                    color: isDarkMode ? "#374151" : "#374151" 
                  }} />
                  <Box>
                    <Typography sx={{ 
                      fontWeight: 600, 
                      color: getMm(isDarkMode).onSurface,
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    }}>
                      {t("please_update_profile_info") || "Please update your profile information before continuing."}
                    </Typography>
                    {!hasParent && (
                      <Typography sx={{ 
                        color: getMm(isDarkMode).onSurfaceVariant, 
                        mt: 0.5,
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}>
                        {t("please_update_profile_info_typeofaccount") || "Please update your profile information before continuing."}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Account Type Selection */}
            {!isDisabled && !hasParent && (
              <Card sx={getCardStyle()}>
                <CardContent sx={{ py: 4, px: 3, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  <Typography sx={{ 
                    mb: 3, 
                    textAlign: "center", 
                    fontWeight: 700, 
                    color: getMm(isDarkMode).onSurface,
                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}>
                    {t("select_account_type") || "Select Account Type"}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <Button variant="outlined"
                     
                      onClick={() => {
                        setIsParent(true);
                        setIsStudent(false);
                      }}
                      sx={toolbarButtonSx}
                      startIcon={<SupervisorAccountIcon />}
                    >
                      {t("parent")}
                    </Button>

                    <Button variant="outlined"
                     
                      onClick={() => {
                        setIsParent(false);
                        setIsStudent(true);
                      }}
                      sx={dialogFormCancelSx}
                      startIcon={<SportsKabaddi />}
                    >
                      {t("student")}
                    </Button>

                    <Button variant="outlined"
                     
                      onClick={() => {
                        setIsParent(true);
                        setIsStudent(true);
                      }}
                      sx={dialogFormCancelSx}
                      startIcon={<SupervisorAccountIcon />}
                    >
                      {t("parent_and_student")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Enhanced Profile Card - MDB Style */}
            <Container 
              maxWidth="md"
              sx={{ 
                mt: 0,
                mb: { xs: 1, sm: 4 },
                pt: { xs: 0, sm: 1 },
                pb: { xs: 0, sm: 10 },
                px: { xs: 0.75, sm: 2 }, // 6px padding on mobile (0.75 * 8px = 6px), keep 16px on larger screens
                width: '100%',
                maxWidth: { xs: '100%', sm: 'md' } // Full width on mobile, constrained on larger screens
              }}
            >
            <Card sx={{
                borderRadius: '8px',
                backgroundColor: getMm(isDarkMode).surfaceLowest,
                boxShadow: 'none',
                border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                overflow: 'hidden',
                mb: { xs: 1.5, sm: 4 },
                maxWidth: PROFILE_CARD_MAX_WIDTH,
                mx: 'auto',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                <CardContent sx={{ 
                  textAlign: 'center',
                  p: { xs: 1.75, sm: 4 },
                  position: 'relative',
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}>
                  {/* Profile Image Section */}
                  <Box sx={{ mt: { xs: 0.5, sm: 3 }, mb: { xs: 1.5, sm: 4 } }}>
                    <Box sx={{ 
                      position: "relative", 
                      display: "inline-block",
                    }}>
                      {tempImagePreview ? (
                        <Avatar
                          src={tempImagePreview}
                          alt="Profile Preview"
                          sx={{ 
                            width: { xs: 112, sm: 240 },
                            height: { xs: 112, sm: 240 },
                            border: isDarkMode ? '4px solid #374151' : '4px solid #fff',
                            boxShadow: 'none'
                          }}
                        />
                      ) : (
                        <SupabaseImageAvatar
                          user={userInfo[0]}
                          size={isMobile ? 112 : 240}
                          refreshKey={imageRefreshKey}
                          sx={{ 
                            border: isDarkMode ? '4px solid #374151' : '4px solid #fff',
                            boxShadow: 'none'
                          }}
                        />
                      )}
                    </Box>
                    </Box>

                    {/* Name */}
                  <Typography sx={{ 
                    fontWeight: 600, 
                    color: getMm(isDarkMode).onSurface, 
                    mb: 0.25,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}>
                    {name}
                  </Typography>
                  {userInfo[0]?.studentID != null && (
                    <Typography sx={{ 
                      color: getMm(isDarkMode).onSurfaceVariant,
                      mb: 0.5,
                      fontSize: '0.8125rem',
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    }}>
                      ID: {userInfo[0].studentID}
                    </Typography>
                  )}
                    
                  {/* Account Type & Academy */}
                  <Typography sx={{ 
                    color: getMm(isDarkMode).onSurfaceVariant,
                    mb: { xs: 1.5, sm: 4 },
                    fontSize: '0.8125rem',
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}>
                      {isParent && isStudent 
                      ? `${t("student_and_parent")} | ${academy}`
                        : isParent 
                        ? `${t("parent")} | ${academy}`
                          : isStudent 
                          ? `${t("student")} | ${academy}`
                          : academy
                      }
                    </Typography>

                  {/* Belt rank (sport-aware: BJJ BeltView, other sports vector bar) */}
                  {isStudent && showMemberBeltRank && belt && (
                    <Box sx={{ mb: { xs: 1.5, sm: 4 }, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
                      <RankBeltDisplay
                        belt={belt}
                        beltSystemKey={validationKey}
                        stripeCount={memberUsesStripeRank ? (userInfo[0]?.beltStripe ?? 0) : 0}
                        width={isMobile ? 200 : 280}
                        height={isMobile ? 20 : 28}
                      />
                      {apiMeLoaded && !shouldUseBeltView(validationKey) && rankUiConfigured ? (
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: getMm(isDarkMode).onSurfaceVariant,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                          }}
                        >
                          {t(`sport_key_${validationKey}`, validationKey)}
                        </Typography>
                      ) : null}
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ mb: { xs: 1.5, sm: 4 }, pb: { xs: 0, sm: 2 }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    {/* Upload Photo Button */}
                    {userInfo[0]?.allowEdit && (
                      <>
                        <input
                          accept="image/*"
                          id="simple-file-input"
                          style={{ display: 'none' }}
                          type="file"
                          onChange={handleImageChange}
                        />
                        <Button variant="outlined"
                         
                          size="small"
                          onClick={() => document.getElementById('simple-file-input').click()}
                          startIcon={<PhotoCameraIcon />}
                          sx={toolbarButtonSx}
                        >
                          {imageName ? t("change_profile_image") : t("upload_profile_image")}
                        </Button>
                        {imageName && imageName !== "Current Image" && (
                          <Typography variant="caption" sx={{ 
                            color: isDarkMode ? '#b0b0b0' : '#7f8c8d',
                            fontSize: '0.75rem',
                            textAlign: 'center'
                          }}>
                            {imageName}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  {/* Address Field - Admin Dashboard Style */}
                  <Box sx={{ mb: { xs: 1.5, sm: 4 }, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    <Typography sx={{ 
                      fontWeight: 600, 
                      color: getMm(isDarkMode).onSurface,
                      mb: 0.75,
                      fontSize: '0.875rem',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      textAlign: 'left',
                    }}>
                      {t("address")}
                    </Typography>
                    {!isDisabled ? (
                      <TextField
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={isMobile ? 1 : 2}
                        size={isMobile ? "small" : "medium"}
                        sx={getTextFieldStyle()}
                        placeholder={t("enter_address") || "Enter address"}
                      />
                    ) : (
                      <Typography sx={{ 
                        color: getMm(isDarkMode).onSurfaceVariant,
                        fontSize: '0.875rem',
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        textAlign: 'left',
                      }}>
                        {address || (t("no_address") || "No address provided")}
                      </Typography>
                    )}
                  </Box>

                  {/* Belt / stripe stats — hidden for belt-less sports (e.g. boxing) */}
                  {isStudent && showMemberBeltRank ? (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: { xs: 3, sm: 4 },
                    mt: { xs: 1.5, sm: 5 }, 
                    mb: 0,
                    pt: { xs: 1.5, sm: 3 },
                    borderTop: `2px solid ${getMm(isDarkMode).outlineVariant}`,
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}>
                    <Box>
                      <Typography sx={{ 
                        fontWeight: 600, 
                        color: getMm(isDarkMode).onSurface,
                        mb: 0.5,
                        fontSize: { xs: '1.0625rem', sm: '1.25rem' },
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}>
                        {belt ? getBeltDisplayName(belt, t) : '-'}
                      </Typography>
                      <Typography sx={{ 
                        color: getMm(isDarkMode).onSurfaceVariant,
                        fontSize: '0.8125rem',
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}>
                        {t("belt")}
                      </Typography>
                    </Box>
                    {memberUsesStripeRank ? (
                    <Box>
                      <Typography sx={{ 
                        fontWeight: 600, 
                        color: getMm(isDarkMode).onSurface,
                        mb: 0.5,
                        fontSize: { xs: '1.0625rem', sm: '1.25rem' },
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}>
                        {userInfo[0]?.beltStripe !== undefined && userInfo[0]?.beltStripe !== null ? userInfo[0].beltStripe : '-'}
                      </Typography>
                      <Typography sx={{ 
                        color: getMm(isDarkMode).onSurfaceVariant,
                        fontSize: '0.8125rem',
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}>
                        {t("stripes")}
                      </Typography>
                    </Box>
                    ) : null}
                  </Box>
                  ) : null}
              </CardContent>
            </Card>
            </Container>

            {/* Modern Action Buttons */}
            {!isDisabled && (
              <Box sx={{ 
                textAlign: "center", 
                mt: { xs: 1, sm: 4 },
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                flexWrap: 'wrap'
              }}>
                <Button variant="outlined"
                  onClick={updateProfile}
                 
                  startIcon={<SaveIcon />}
                  sx={toolbarButtonSx}
                  size={isMobile ? "medium" : "large"}
                >
                  {t("update_profile")}
                </Button>
              </Box>
            )}
          </Box>
        </Fade>
      )}
      {/* Change Password Dialog */}
      <Dialog
        open={changePasswordDialogOpen}
        onClose={() => !changePasswordLoading && setChangePasswordDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            backgroundColor: getMm(isDarkMode).surfaceLowest,
            border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
            fontFamily: "'Plus Jakarta Sans', sans-serif"
          }
        }}
      >
        <DialogTitle sx={{ color: getMm(isDarkMode).onSurface, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon sx={{ fontSize: '1.25rem' }} />
          {t("change_password") || "Change password"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ color: getMm(isDarkMode).onSurfaceVariant, mb: 2, fontSize: '0.875rem', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {t("change_password_description") || "Set a new password for your account."}
          </Typography>
          <TextField
            label={t("change_password_new") || "New password"}
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ ...getTextFieldStyle(), mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" sx={{ color: getMm(isDarkMode).onSurfaceVariant }}>
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label={t("change_password_confirm") || "Confirm new password"}
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ ...getTextFieldStyle() }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{ color: getMm(isDarkMode).onSurfaceVariant }}>
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => !changePasswordLoading && setChangePasswordDialogOpen(false)}
            disabled={changePasswordLoading}
            sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button variant="outlined"
           
            onClick={handleChangePassword}
            disabled={changePasswordLoading || !newPassword || !confirmPassword}
            sx={dialogFormCancelSx}
          >
            {changePasswordLoading ? (t("change_password_updating") || "Updating...") : (t("change_password_btn") || "Update password")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteAccountDialogOpen}
        onClose={closeDeleteAccountDialog}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            backgroundColor: getMm(isDarkMode).surfaceLowest,
            border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: getMm(isDarkMode).onSurface,
            fontWeight: 600,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: "1.25rem", color: "#DC2626" }} />
          {t("delete_account_warning_title")}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              backgroundColor: isDarkMode ? "rgba(245, 158, 11, 0.12)" : undefined,
              color: isDarkMode ? "#FDE68A" : undefined,
              "& .MuiAlert-icon": { color: isDarkMode ? "#FBBF24" : undefined },
            }}
          >
            {t("delete_account_warning_alert")}
          </Alert>
          <Typography
            component="div"
            sx={{
              color: getMm(isDarkMode).onSurfaceVariant,
              mb: 2,
              fontSize: "0.875rem",
              lineHeight: 1.6,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
              <Box component="li" sx={{ mb: 0.75 }}>{t("delete_account_warning_point_login")}</Box>
              <Box component="li" sx={{ mb: 0.75 }}>{t("delete_account_warning_point_data")}</Box>
              <Box component="li">{t("delete_account_warning_point_finance")}</Box>
            </Box>
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteAccountAcknowledged}
                onChange={(e) => setDeleteAccountAcknowledged(e.target.checked)}
                sx={{
                  color: getMm(isDarkMode).onSurfaceVariant,
                  "&.Mui-checked": { color: "#DC2626" },
                }}
              />
            }
            label={t("delete_account_acknowledge")}
            sx={{
              alignItems: "flex-start",
              mx: 0,
              "& .MuiFormControlLabel-label": {
                fontSize: "0.875rem",
                lineHeight: 1.5,
                color: getMm(isDarkMode).onSurface,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={closeDeleteAccountDialog}
            disabled={deleteAccountLoading}
            sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="outlined"
            onClick={handleDeleteAccount}
            disabled={deleteAccountLoading || !deleteAccountAcknowledged}
            sx={dialogFormDestructiveSx}
          >
            {deleteAccountLoading ? t("loading") : t("delete_my_account_confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarNotification
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default ProfilePage;